import { Box, Switch, TextField, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import ExhibitionOriginChip from 'src/components/exhibition-origin-chip';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';

type BulkEditableBenefitRow = {
  benefitId: string;
  benefitType: 'coupon' | 'product';
  image: string;
  name: string;
  priority: number | null;
};

type BulkEditFieldChange = (
  row: BulkEditableBenefitRow,
  field: 'name' | 'priority',
  value: string | number | null
) => void;

type GetBenefitsColumnsParams = {
  formatDate: (isoDate: string) => string;
  formatPoints: (points: number) => string;
  handleChangeStatus: (benefitId: string, newStatus: 'active' | 'inactive') => void;
  tierColumnWidth: number;
  isBulkEditMode?: boolean;
  isBulkEditLocked?: boolean;
  onBulkEditFieldChange?: BulkEditFieldChange;
};

const BULK_EDIT_INPUT_COMMIT_DELAY_MS = 120;

const renderReadOnlyTextCell = (value: string | number) => (
  <Tooltip title={value} slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}>
    <Box
      component="span"
      sx={{
        width: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Box>
  </Tooltip>
);

const useBufferedInput = (externalValue: string, onCommit: (value: string) => void) => {
  const [value, setValue] = useState(externalValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef(externalValue);
  const lastEmittedValueRef = useRef(externalValue);
  const onCommitRef = useRef(onCommit);

  onCommitRef.current = onCommit;

  useEffect(() => {
    if (externalValue === lastEmittedValueRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    latestValueRef.current = externalValue;
    lastEmittedValueRef.current = externalValue;
    setValue(externalValue);
  }, [externalValue]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const commit = useCallback((nextValue: string) => {
    lastEmittedValueRef.current = nextValue;
    onCommitRef.current(nextValue);
  }, []);

  const setBufferedValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue);
      latestValueRef.current = nextValue;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        commit(nextValue);
      }, BULK_EDIT_INPUT_COMMIT_DELAY_MS);
    },
    [commit]
  );

  const flush = useCallback(() => {
    const hasPendingCommit = Boolean(timeoutRef.current);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const nextValue = latestValueRef.current;
    if (
      hasPendingCommit ||
      (nextValue !== externalValue && nextValue !== lastEmittedValueRef.current)
    ) {
      flushSync(() => commit(nextValue));
    }
  }, [commit, externalValue]);

  return { value, setBufferedValue, flush };
};

type BulkEditCellProps = {
  row: BulkEditableBenefitRow;
  disabled: boolean;
  onChange?: BulkEditFieldChange;
};

const BenefitNameEditCell = memo(({ row, disabled, onChange }: BulkEditCellProps) => {
  const { value, setBufferedValue, flush } = useBufferedInput(row.name, (nextValue) => {
    onChange?.(row, 'name', nextValue);
  });

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      error={!value.trim()}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={flush}
      onChange={(event) => setBufferedValue(event.target.value)}
      inputProps={{ 'aria-label': `Nome do benefício ${row.benefitId}` }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: 38,
          bgcolor: 'common.white',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
        },
      }}
    />
  );
});

BenefitNameEditCell.displayName = 'BenefitNameEditCell';

const BenefitPriorityEditCell = memo(({ row, disabled, onChange }: BulkEditCellProps) => {
  const externalValue = row.priority === null ? '' : String(row.priority);
  const { value, setBufferedValue, flush } = useBufferedInput(externalValue, (nextValue) => {
    onChange?.(row, 'priority', nextValue === '' ? null : Number(nextValue));
  });

  const handleChange = (rawValue: string) => {
    if (rawValue === '') {
      setBufferedValue(rawValue);
      return;
    }

    if (!/^\d+$/.test(rawValue)) {
      return;
    }

    const priority = Number(rawValue);
    if (Number.isSafeInteger(priority)) {
      setBufferedValue(rawValue);
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      value={value}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={flush}
      onChange={(event) => handleChange(event.target.value)}
      inputProps={{
        'aria-label': `Prioridade do benefício ${row.benefitId}`,
        min: 0,
        step: 1,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: 38,
          bgcolor: 'common.white',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
        },
      }}
    />
  );
});

BenefitPriorityEditCell.displayName = 'BenefitPriorityEditCell';

const isEmptyDateValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  return normalizedValue === '' || normalizedValue === 'null' || normalizedValue === 'undefined';
};

const renderDateCell = (value: unknown, formatDate: (isoDate: string) => string) => {
  if (isEmptyDateValue(value) || Number.isNaN(new Date(String(value)).getTime())) {
    return (
      <Label
        variant="soft"
        color="default"
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          color: 'text.secondary',
          fontWeight: 700,
        }}
      >
        Sem data
      </Label>
    );
  }

  return (
    <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
      <Label
        variant="outlined"
        sx={{
          gap: 0.75,
          px: 1.4,
          py: 0.45,
          display: 'inline-flex',
          alignItems: 'center',
          fontWeight: 700,
        }}
      >
        <Iconify icon="mdi:calendar-month-outline" sx={{ width: 16, height: 16 }} />
        {formatDate(String(value))}
      </Label>
    </Box>
  );
};

function translateTier(tier?: string) {
  switch (tier?.toLowerCase()) {
    case 'all':
      return 'Todos';
    case 'silver':
      return 'prata';
    case 'gold':
      return 'ouro';
    case 'diamond':
      return 'diamante';
    default:
      return tier || 'Sem Tier';
  }
}

const tierStyles: Record<string, any> = {
  todos: {
    background: 'linear-gradient(180deg, #6f7680 0%, #4e5660 100%)',
    color: '#ffffff',
    boxShadow:
      '0 2px 6px rgba(33, 40, 48, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.18), inset 0 -1px 1px rgba(0, 0, 0, 0.18)',
    textShadow: '0 1px 1px rgba(0, 0, 0, 0.35)',
  },
  prata: {
    background:
      'linear-gradient(135deg, #f0f0f0 0%, #d9d9d9 20%, #bfbfbf 40%, #e6e6e6 60%, #c0c0c0 80%, #f5f5f5 100%)',
    color: '#1a1a1a',
    boxShadow:
      '0 2px 6px rgba(160, 160, 160, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.7), inset 0 -1px 2px rgba(120, 120, 120, 0.2)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.8)',
  },
  ouro: {
    background:
      'linear-gradient(135deg, #fff6d0 0%, #ffec99 20%, #ffd84d 40%, #f5c842 60%, #eec44d 80%, #fff2b8 100%)',
    color: '#3a2d00',
    boxShadow:
      '0 2px 6px rgba(230, 180, 60, 0.5), inset 0 1px 2px rgba(255, 245, 200, 0.7), inset 0 -1px 2px rgba(190, 150, 40, 0.25)',
    textShadow: '0 1px 1px rgba(255, 250, 210, 0.85)',
  },
  diamante: {
    background:
      'linear-gradient(135deg, #e0f7ff 0%, #b8eaff 20%, #a0e0f0 40%, #c8f4ff 60%, #e6fbff 80%, #f4fdff 100%)',
    color: '#004040',
    boxShadow:
      '0 2px 8px rgba(140, 220, 240, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(120, 180, 200, 0.3)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.85)',
  },
};

export const getBenefitsColumns = ({
  formatDate,
  formatPoints,
  handleChangeStatus,
  tierColumnWidth,
  isBulkEditMode = false,
  isBulkEditLocked = false,
  onBulkEditFieldChange,
}: GetBenefitsColumnsParams): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: 'image',
      headerName: 'Imagem',
      width: 112,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const image = String(params.value || '');

        if (!image) {
          return '-';
        }

        return (
          <Tooltip
            arrow
            placement="left"
            title={
              <Box
                component="img"
                src={image}
                alt={String(params.row?.name || 'Benefício')}
                sx={{
                  display: 'block',
                  width: 240,
                  maxHeight: 240,
                  objectFit: 'contain',
                  borderRadius: 0.75,
                }}
              />
            }
            slotProps={{
              tooltip: {
                sx: {
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: 'common.white',
                  boxShadow: 8,
                },
              },
            }}
          >
            <Box
              component="img"
              src={image}
              alt={String(params.row?.name || 'Benefício')}
              sx={{
                display: 'block',
                width: 72,
                height: 40,
                borderRadius: 0.75,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 220,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (!isBulkEditMode) {
          return renderReadOnlyTextCell(String(params.value || '').trim());
        }

        return (
          <BenefitNameEditCell
            row={params.row}
            disabled={isBulkEditLocked}
            onChange={onBulkEditFieldChange}
          />
        );
      },
    },
    {
      field: 'priority',
      headerName: 'Prioridade',
      minWidth: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (!isBulkEditMode) {
          return renderReadOnlyTextCell(
            typeof params.value === 'number' && Number.isFinite(params.value) ? params.value : '-'
          );
        }

        return (
          <BenefitPriorityEditCell
            row={params.row}
            disabled={isBulkEditLocked}
            onChange={onBulkEditFieldChange}
          />
        );
      },
    },
    {
      field: 'company',
      headerName: 'Empresa',
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'tier',
      headerName: 'Tier',
      headerAlign: 'left',
      align: 'left',
      width: tierColumnWidth,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const rowTiers = Array.isArray(params.row.visibleForTiers)
          ? params.row.visibleForTiers
          : [];
        let normalizedTierValues = [String(params.row.tier || 'all')];

        if (rowTiers.length > 0) {
          normalizedTierValues = rowTiers.includes('all') ? ['all'] : rowTiers;
        }

        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, width: 1 }}>
            {normalizedTierValues.map((tierValue: string) => {
              const translatedTier = translateTier(tierValue);
              const capitalizedTier =
                translatedTier.charAt(0).toUpperCase() + translatedTier.slice(1).toLowerCase();

              return (
                <Tooltip
                  key={`${params.id}-${tierValue}`}
                  title={capitalizedTier}
                  slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}
                >
                  <Label
                    variant="soft"
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      ...tierStyles[translatedTier.toLowerCase()],
                    }}
                  >
                    {translatedTier}
                  </Label>
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'pointsValue',
      headerName: 'Valor (em pontos)',
      minWidth: 160,
      flex: 0.9,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: ({ value }: { value: unknown }) => formatPoints(Number(value || 0)),
    },
    {
      field: 'type',
      headerName: 'Tipo',
      minWidth: 180,
      flex: 0.9,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const rawType = String(params.value || '').toLowerCase();
        const isCoupon = rawType === 'cupom';

        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.6,
              py: 0.6,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'grey.300',
              backgroundColor: 'grey.100',
              color: 'text.primary',
              fontWeight: 600,
              minWidth: 110,
            }}
          >
            {isCoupon ? (
              <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} />
            ) : (
              <Iconify icon="mdi:cube-outline" sx={{ width: 18, height: 18 }} />
            )}
            <Box component="span">{isCoupon ? 'Cupom' : 'Produto'}</Box>
          </Box>
        );
      },
    },
    {
      field: 'exhibitionEnv',
      headerName: 'Origem',
      minWidth: 160,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => <ExhibitionOriginChip value={params.value} />,
    },
    {
      field: 'isNewBenefit',
      headerName: 'Novidade?',
      sortable: false,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Label variant="soft" color={params.value === true ? 'info' : 'default'}>
          {params.value === true ? 'Sim' : 'Não'}
        </Label>
      ),
    },
    {
      field: 'totalQuantity',
      headerName: 'Quantidade total',
      minWidth: 170,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isParking) {
          return '-';
        }

        return String(params.value ?? '-');
      },
    },
    {
      field: 'availableQuantity',
      headerName: 'Quantidade disponível',
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isParking) {
          return '-';
        }

        return String(params.value ?? '-');
      },
    },
    {
      field: 'redeemedQuantity',
      headerName: 'Quantidade resgatada',
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'withdrawnQuantity',
      headerName: 'Quantidade retirada',
      minWidth: 180,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'startDate',
      headerName: 'Data de início',
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => renderDateCell(params.value, formatDate),
    },
    {
      field: 'endDate',
      headerName: 'Data de fim',
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => renderDateCell(params.value, formatDate),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      renderHeader: () => (
        <Tooltip
          placement="top"
          slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}
          title={
            <Box>
              <Box>
                <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                  Ativo:
                </Box>{' '}
                beneficio ativo e disponível para resgate.
              </Box>
              <Box>
                <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                  Inativo
                </Box>{' '}
                beneficio desativado manualmente.
              </Box>
              <Box>
                <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                  Expirado:
                </Box>{' '}
                beneficio fora do período de validade.
              </Box>
              <Box>
                <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
                  Esgotado:
                </Box>{' '}
                todas as unidades disponíveis ja foram utilizadas.
              </Box>
            </Box>
          }
        >
          <Box component="span">Status</Box>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const rawStatus = String(params.value || '')
          .toLowerCase()
          .trim();

        if (rawStatus === 'expired') {
          return (
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Label variant="soft" color="error">
                Expirado
              </Label>
            </Box>
          );
        }

        if (rawStatus === 'soldout' || rawStatus === 'sold_out' || rawStatus === 'sold out') {
          return (
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Label variant="soft" color="warning">
                Esgotado
              </Label>
            </Box>
          );
        }

        if (rawStatus === 'active' || rawStatus === 'inactive') {
          return (
            <Switch
              checked={rawStatus === 'active'}
              disabled={isBulkEditMode}
              onClick={(e) => e.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation();
                const nextStatus = event.target.checked ? 'active' : 'inactive';
                const benefitId = String(params.row.benefitId || params.id || '');

                handleChangeStatus(benefitId, nextStatus);
              }}
              sx={{
                width: 65,
                height: 25,
                p: 0,
                overflow: 'visible',

                '& .MuiSwitch-switchBase': {
                  p: '4px',
                  transitionDuration: '220ms',

                  '&.Mui-checked': {
                    transform: 'translateX(35px)',
                    color: 'common.white',

                    '& + .MuiSwitch-track': {
                      borderColor: 'success.main',
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      boxShadow: (theme) =>
                        `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 8px 18px ${alpha(theme.palette.success.main, 0.24)}`,
                    },

                    '& + .MuiSwitch-track::before': {
                      opacity: 1,
                    },

                    '& + .MuiSwitch-track::after': {
                      opacity: 0,
                    },
                  },

                  '&.Mui-focusVisible .MuiSwitch-thumb': {
                    outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    outlineOffset: 2,
                  },
                },

                '& .MuiSwitch-thumb': {
                  width: 17,
                  height: 17,
                  bgcolor: 'common.white',
                  boxShadow: (theme) =>
                    `0 3px 10px ${alpha(theme.palette.grey[900], 0.22)}, inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                },

                '& .MuiSwitch-track': {
                  position: 'relative',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'error.main',
                  opacity: 1,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.darker} 100%)`,
                  boxShadow: (theme) =>
                    `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.16)}, 0 8px 18px ${alpha(theme.palette.error.main, 0.2)}`,

                  '&::before': {
                    content: '"Ativo"',
                    position: 'absolute',
                    left: 9,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    fontSize: 9,
                    lineHeight: 1,
                    fontWeight: 700,
                    letterSpacing: 0,
                    opacity: 0,
                    transition: 'opacity 160ms ease',
                  },

                  '&::after': {
                    content: '"Inativo"',
                    position: 'absolute',
                    right: 7,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    fontSize: 9,
                    lineHeight: 1,
                    fontWeight: 700,
                    letterSpacing: 0,
                    opacity: 1,
                    transition: 'opacity 160ms ease',
                  },
                },
              }}
            />
          );
        }

        return String(params.value || '');
      },
    },
  ];

  return columns.map((column) => {
    const isEditableColumn = column.field === 'name' || column.field === 'priority';
    const { cellClassName: defaultCellClassName } = column;
    let cellClassName = defaultCellClassName;

    if (isBulkEditMode) {
      cellClassName = isEditableColumn
        ? 'benefits-bulk-edit-cell'
        : 'benefits-bulk-edit-readonly-cell';
    }

    return {
      ...column,
      disableColumnMenu: true,
      cellClassName,
      renderCell: isBulkEditMode
        ? column.renderCell
        : (params: GridRenderCellParams) => {
            const content = column.renderCell
              ? column.renderCell(params)
              : (params.formattedValue ?? params.value ?? '');

            return typeof content === 'string' || typeof content === 'number'
              ? renderReadOnlyTextCell(content)
              : content;
          },
    };
  });
};
